/**
 * Shared reconnect policy for every SSE consumer (dashboard useNotifications +
 * CommentThread, extension notificationSSE + commentSSE).
 *
 * Two server paths emit a cap `error` event then close. A stream can receive
 * it at OPEN (rejected after responding 200, without ever sending its success
 * event: `heartbeat` for notifications, `connected` for comments) or, since
 * 2026-08-04, MID-STREAM (evicted: the per-user caps close the user's OLDEST
 * stream to admit their newest, so the surface the user is actively looking
 * at is never the one that goes dead):
 *
 *   - routes/notifications.ts    -> services/notificationBroadcast.ts (10 per user, evicts; 200 global, rejects)
 *   - routes/issueCommentSSE.ts  -> services/commentBroadcast.ts     (10 per user per issue, evicts; 50 per issue, rejects)
 *
 * EventSource surfaces that as an "error" event, which is the SAME event type a
 * network drop uses. A handler that ignores the payload cannot distinguish
 * "you have too many tabs open" from "your wifi hiccupped", so it reconnects on
 * the normal cadence, forever. Incident 2026-07-05: one user, 16+ minutes,
 * 29 escalated $exceptions, and both the 60/min SSE bucket and the 100/min
 * global bucket exhausted (see debug_log).
 *
 * The cap is self-healing (closing a tab frees a slot), so the correct response
 * is a long cooldown, never a permanent stop.
 */
/** Code the server sends in the `error` event payload when a cap is hit. */
export const SSE_CONNECTION_CAP_CODE = 'TOO_MANY_CONNECTIONS';
/**
 * Retry cadence while capped. Must exceed every consumer's normal max backoff
 * (30s) so a capped client stops competing for the per-user SSE bucket, while
 * staying short enough that freeing a slot recovers within a minute.
 */
export const SSE_CONNECTION_CAP_COOLDOWN_MS = 60_000;
/**
 * Extract the `code` from a server-sent SSE `error` event payload.
 * Returns null for transport errors (bare Event, no `data`) and for any
 * malformed payload. Never throws: it runs inside an EventSource error handler.
 */
export function parseSseErrorCode(event) {
    const data = event?.data;
    if (typeof data !== 'string' || data.length === 0)
        return null;
    let parsed;
    try {
        parsed = JSON.parse(data);
    }
    catch {
        return null;
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed))
        return null;
    const code = parsed.code;
    return typeof code === 'string' ? code : null;
}
/** True when the server rejected this stream because a connection cap was hit. */
export function isConnectionCapError(event) {
    return parseSseErrorCode(event) === SSE_CONNECTION_CAP_CODE;
}
/**
 * Backoff to use after a stream-ticket fetch fails. A 429 means we are already
 * rate-limited, so jump straight to the max delay rather than retrying on the
 * base cadence and adding to the storm. Any other status keeps the caller's
 * existing exponential backoff.
 *
 * Monotone on 429: a client already sitting on the longer cap cooldown must not
 * be pulled back down to `maxDelayMs`.
 */
export function resolveTicketFailureDelay(status, currentDelayMs, maxDelayMs) {
    return status === 429 ? Math.max(currentDelayMs, maxDelayMs) : currentDelayMs;
}
//# sourceMappingURL=sseReconnectPolicy.js.map